import * as React from 'react';
import { THEME_MODE_KEY, THEME_NAME_KEY, applyThemeClasses, parseMode, resolveMode } from './init';
import {
  DEFAULT_THEME_ID,
  type ResolvedMode,
  type ThemeId,
  type ThemeMode,
  resolveThemeId,
} from './registry';

interface ThemeContextValue {
  mode: ThemeMode;
  resolvedMode: ResolvedMode;
  theme: ThemeId;
  setMode: (mode: ThemeMode) => void;
  setTheme: (theme: ThemeId) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * Owns the `(mode, theme)` pair and writes both to `<html>`'s class list
 * + `localStorage`. The blocking script in `index.html` has already
 * applied the right classes by the time this mounts — the provider's job
 * is to keep them in sync as the user toggles, and to subscribe to the
 * OS-level `prefers-color-scheme` change when `mode === 'system'`.
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const [mode, setModeState] = React.useState<ThemeMode>(() => parseMode(safeRead(THEME_MODE_KEY)));
  const [theme, setThemeState] = React.useState<ThemeId>(() =>
    resolveThemeId(safeRead(THEME_NAME_KEY)),
  );

  const prefersDark = useSystemPrefersDark();
  const resolvedMode: ResolvedMode = resolveMode(mode, prefersDark);

  // Apply classes on every (mode/theme/prefersDark) change.
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    applyThemeClasses(document.documentElement, resolvedMode, theme);
  }, [resolvedMode, theme]);

  const setMode = React.useCallback((next: ThemeMode) => {
    setModeState(next);
    safeWrite(THEME_MODE_KEY, next);
  }, []);

  const setTheme = React.useCallback((next: ThemeId) => {
    const safeId = resolveThemeId(next);
    setThemeState(safeId);
    safeWrite(THEME_NAME_KEY, safeId);
  }, []);

  const value = React.useMemo<ThemeContextValue>(
    () => ({ mode, resolvedMode, theme, setMode, setTheme }),
    [mode, resolvedMode, theme, setMode, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (ctx === null) {
    throw new Error('useTheme must be called inside <ThemeProvider>.');
  }
  return ctx;
}

export { DEFAULT_THEME_ID };

function useSystemPrefersDark(): boolean {
  const subscribe = React.useCallback((cb: () => void): (() => void) => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return () => {};
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    mql.addEventListener('change', cb);
    return () => mql.removeEventListener('change', cb);
  }, []);

  const getSnapshot = React.useCallback((): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, []);

  // SSR snapshot — defaulting to `false` (light) avoids hydration
  // mismatches; the post-hydration `getSnapshot` corrects it.
  const getServerSnapshot = React.useCallback((): boolean => false, []);

  return React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

function safeRead(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private-mode Safari throws when storage is full or disabled.
    // Failing silently is correct — the in-memory state still updates.
  }
}
